import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, filter, map, Observable, Subject } from 'rxjs';
import { TokenService } from 'src/app/compartido/autenticacion/token.service';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { environment } from 'src/environments/environment';

/* Tiempo de reintentos de reconexión antes de reportar el error */
const VENTANA_RECONEXION = 7 * 60 * 1000;

/* Espera inicial y máxima entre reintentos de conexión */
const ESPERA_INICIAL = 1000;
const ESPERA_MAXIMA = 60000;

/* Intervalo del ping para mantener la conexión viva */
const INTERVALO_PING = 30000;

/* Retraso de un reintento a partir del cual se asume que el equipo estuvo suspendido */
const TOLERANCIA_TIMER = 5000;

/* Máximo de mensajes en cola mientras el socket está desconectado */
const MAXIMO_PENDIENTES = 50;

/* Endpoint público y liviano con el que se sondea si el servidor responde por HTTP */
const URL_SONDEO = `${environment.apiUrl}/analiticas/version_movil/`;

/* Tiempo máximo de espera del sondeo HTTP */
const TIMEOUT_SONDEO = 5000;

interface EntradaSocket {
  socket: WebSocket | undefined;
  url: string;
  autenticar: boolean;
  listo: boolean;
  pingTimer: ReturnType<typeof setInterval> | undefined;
  reconexionTimer: ReturnType<typeof setTimeout> | undefined;
  intentos: number;
  desconectadoDesde: number | undefined;
  reportado: boolean;
  sondeando: boolean;
  pendientes: DatosWebsocket[];
  resolver: (() => void) | undefined;
}

export interface DatosWebsocket {
  tipo: string;
  contenido?: any;
}

@Injectable({ providedIn: 'root' })
export class WebSocketsService implements OnDestroy {
  /* Websockets */
  private _sockets: Record<string, EntradaSocket> = {};

  /* Mensajes Recibidos */
  private _mensajes$ = new Subject<{ id: string; contenido: any }>();

  /* Sockets que el servicio cerró porque la sesión terminó */
  private _cerradosPorSesion = new Set<string>();

  /* Momento en que la pestaña pasó a segundo plano */
  private _ocultoDesde: number | undefined;

  /* Reconecta los websockets pendientes al recuperarse la conexión a internet */
  private readonly _alRecuperarConexion = (): void => {
    // El tiempo sin internet no cuenta como falla del servidor
    for (const entrada of Object.values(this._sockets)) {
      if (!entrada.listo) entrada.desconectadoDesde = Date.now();
    }
    this._reintentarAhora();
  };

  /* Reconecta los websockets pendientes al volver la pestaña a primer plano */
  private readonly _alCambiarVisibilidad = (): void => {
    if (document.visibilityState === 'hidden') {
      this._ocultoDesde = Date.now();
      return;
    }

    // Con la pestaña oculta el navegador congela los timers, así que ese rato no hubo
    // reintentos y contarlo hace pasar por caída del servidor lo que solo fue un equipo
    // suspendido. Se descuenta en vez de reiniciar el contador, para que una caída real
    // que empezó con la pestaña visible siga acumulando y se acabe reportando.
    const oculto = this._ocultoDesde === undefined ? 0 : Date.now() - this._ocultoDesde;
    this._ocultoDesde = undefined;
    if (oculto > 0) {
      for (const entrada of Object.values(this._sockets)) {
        if (entrada.desconectadoDesde === undefined) continue;
        entrada.desconectadoDesde = Math.min(Date.now(), entrada.desconectadoDesde + oculto);
      }
    }

    this._reintentarAhora();
  };

  constructor(private readonly _tokenService: TokenService) {
    window.addEventListener('online', this._alRecuperarConexion);
    document.addEventListener('visibilitychange', this._alCambiarVisibilidad);
  }

  /**
   * Crea un websocket con el id y la url especificados.
   * Si la conexión se pierde, se reintenta automáticamente; el error solo se
   * reporta si no logra reconectarse dentro de la ventana de reconexión.
   * @param {string} id - El id del websocket a crear.
   * @param {string} url - La url del websocket a crear.
   * @param {boolean} autenticar - Si se debe autenticar el websocket con JWT al abrir.
   * @returns {Promise<void>} - Una promesa que se resuelve cuando el websocket queda listo para enviar mensajes, o cuando se agota la ventana de reconexión (promise).
   * @author Juan Corral
   */
  public async crearSocket$(id: string, url: string, autenticar: boolean = false): Promise<void> {
    if (this._sockets[id]) return;
    this._cerradosPorSesion.delete(id);

    this._sockets[id] = {
      socket: undefined,
      url,
      autenticar,
      listo: false,
      pingTimer: undefined,
      reconexionTimer: undefined,
      intentos: 0,
      desconectadoDesde: undefined,
      reportado: false,
      sondeando: false,
      pendientes: [],
      resolver: undefined,
    };

    const promesa = new Promise<void>((resolve) => {
      this._sockets[id].resolver = resolve;
    });
    this._conectar(id);
    return promesa;
  }

  /**
   * Cierra el websocket con el id especificado.
   * @param {string} id - El id del websocket a cerrar.
   * @author Juan Corral
   */
  public cerrarSocket(id: string): void {
    const entrada = this._sockets[id];
    if (!entrada) return;

    // Eliminar la entrada primero para que los handlers ignoren el cierre
    delete this._sockets[id];
    this._limpiarTimers(entrada);
    if (entrada.socket) {
      entrada.socket.onopen = null;
      entrada.socket.onclose = null;
      entrada.socket.onmessage = null;
      entrada.socket.close();
    }
  }

  /**
   * Envía un mensaje al websocket con el id especificado.
   * Si el websocket está reconectándose, el mensaje se encola y se envía al
   * restablecerse la conexión.
   * @param {string} id - El id del websocket al que enviar el mensaje.
   * @param {DatosWebsocket} datos - El mensaje a enviar al websocket.
   * @author Juan Corral
   */
  public enviarMensaje(id: string, datos: DatosWebsocket): void {
    const entrada = this._sockets[id];
    if (!entrada) {
      // Si el socket lo cerró el servicio al acabarse la sesión, quien lo usaba no tiene
      // cómo enterarse: el mensaje se descarta en silencio en vez de reventarle encima.
      if (this._cerradosPorSesion.has(id)) return;
      throw new ErrorFrontend(`No existe un socket con id ${id}`);
    }

    if (entrada.listo && this._enviarDirecto(entrada, datos)) return;

    // Encolar el mensaje mientras el websocket se reconecta
    if (entrada.pendientes.length < MAXIMO_PENDIENTES) entrada.pendientes.push(datos);
  }

  /**
   * Escucha los mensajes del websocket con el id especificado.
   * @param {string} id - El id del websocket del que escuchar los mensajes.
   * @returns {Observable<any>} - Un observable que emite el contenido de los mensajes recibidos del websocket.
   * @author Juan Corral
   */
  public escucharMensajes$(id: string): Observable<any> {
    if (!this._sockets[id]) {
      if (this._cerradosPorSesion.has(id)) return EMPTY;
      throw new ErrorFrontend(`No existe un socket con id ${id}`);
    }
    return this._mensajes$.asObservable().pipe(
      filter((mensaje) => mensaje.id === id),
      map((mensaje) => mensaje.contenido),
    );
  }

  /**
   * Abre la conexión del websocket con el id especificado y engancha sus handlers.
   * @param {string} id - El id del websocket a conectar.
   */
  private _conectar(id: string): void {
    const entrada = this._sockets[id];
    if (!entrada) return;

    const socket = new WebSocket(entrada.url);
    entrada.socket = socket;
    entrada.listo = false;

    // Escuchar mensajes del websocket
    socket.onmessage = (evento: MessageEvent) => {
      if (!this._esVigente(id, socket) || !evento.data) return;

      let datos: DatosWebsocket;
      try {
        datos = JSON.parse(evento.data as string) as DatosWebsocket;
      } catch {
        console.error(`[WebSocket:${id}] Mensaje con formato inválido`);
        return;
      }

      switch (datos.tipo) {
        case 'autenticar':
          this._marcarConectado(id);
          return;
        case 'conexion':
        case 'pong':
          return;
        case 'error':
          console.error(`[WebSocket:${id}] Error del servidor:`, datos);
          return;
      }

      this._mensajes$.next({ id, contenido: datos.contenido });
    };

    // Re-conectar el websocket si se cierra
    socket.onclose = (ev: CloseEvent) => {
      if (!this._esVigente(id, socket)) return;
      this._limpiarPing(entrada);
      entrada.socket = undefined;
      entrada.listo = false;

      // Reconectar siempre: los cierres iniciados por el cliente no llegan
      // aquí (cerrarSocket elimina la entrada y los handlers antes de cerrar),
      // y el servidor o el proxy pueden cerrar con 1000 en reinicios/timeouts
      this._programarReconexion(id, ev.code);
    };

    socket.onopen = () => {
      if (!this._esVigente(id, socket)) return;

      // Con autenticación el socket queda listo al confirmarla el servidor
      if (entrada.autenticar) {
        this._enviarDirecto(entrada, {
          tipo: 'autenticar',
          contenido: { token: this._tokenService.accessToken },
        });
      } else {
        this._marcarConectado(id);
      }
    };
  }

  /**
   * Marca el websocket como listo, reinicia los reintentos, envía los mensajes
   * encolados e inicia el ping.
   * @param {string} id - El id del websocket.
   */
  private _marcarConectado(id: string): void {
    const entrada = this._sockets[id];
    if (!entrada) return;

    entrada.listo = true;
    entrada.intentos = 0;
    entrada.desconectadoDesde = undefined;
    entrada.reportado = false;

    // Enviar los mensajes encolados durante la desconexión
    const pendientes = entrada.pendientes;
    entrada.pendientes = [];
    for (const datos of pendientes) this._enviarDirecto(entrada, datos);

    this._iniciarPing(id);
    this._resolverEspera(entrada);
  }

  /**
   * Programa el siguiente intento de reconexión del websocket, reportando el
   * error si ya se agotó la ventana de reconexión.
   * @param {string} id - El id del websocket.
   * @param {number} codigo - El código de cierre de la última conexión.
   */
  private _programarReconexion(id: string, codigo: number): void {
    const entrada = this._sockets[id];
    if (!entrada) return;

    if (entrada.desconectadoDesde === undefined) entrada.desconectadoDesde = Date.now();

    // Reportar el error si los reintentos superaron la ventana de reconexión. Sin
    // internet la desconexión no es atribuible al servidor, y con la pestaña oculta
    // tampoco: el navegador estrangula los timers y la red de un equipo que se va a
    // dormir o que lleva horas en segundo plano no es la del servidor. Ese tiempo se
    // descuenta al volver la pestaña a primer plano, y si la caída sigue entonces,
    // se reporta tras la ventana completa con la pestaña visible. Aun así, `onLine`
    // solo dice que hay red local, no salida a internet (Wi-Fi sin internet, VPN,
    // portal cautivo): antes de reportar se sondea el servidor por HTTP, y solo si
    // responde la caída es del websocket (ver `_confirmarYReportar$`).
    const transcurrido = Date.now() - entrada.desconectadoDesde;
    if (
      transcurrido >= VENTANA_RECONEXION &&
      !entrada.reportado &&
      !entrada.sondeando &&
      navigator.onLine &&
      document.visibilityState !== 'hidden'
    ) {
      entrada.reportado = true;
      entrada.pendientes = [];
      this._resolverEspera(entrada);
      void this._confirmarYReportar$(id, entrada, codigo);
    }

    // Reintentar con un tiempo de espera creciente
    const espera = Math.min(ESPERA_INICIAL * 2 ** entrada.intentos, ESPERA_MAXIMA);
    entrada.intentos++;
    const programadoEn = Date.now();
    entrada.reconexionTimer = setTimeout(async () => {
      if (!this._sockets[id]) return;

      // Un equipo suspendido congela los timers pero no el reloj: si el reintento se
      // ejecuta mucho después de lo programado, ese rato no tuvo reintentos y no es
      // atribuible al servidor, así que se descuenta. Es el mismo descuento que hace
      // `visibilitychange`, pero suspender el equipo no siempre dispara ese evento.
      const dormido = Date.now() - programadoEn - espera;
      if (dormido > TOLERANCIA_TIMER && entrada.desconectadoDesde !== undefined) {
        entrada.desconectadoDesde = Math.min(Date.now(), entrada.desconectadoDesde + dormido);
      }

      // Refrescar el token si el cierre fue por un fallo de autenticación
      if (codigo >= 4000 && entrada.autenticar) {
        const refrescado = await this._tokenService.refrescarToken$();

        // Si la sesión ya no existe, seguir reintentando solo golpea al servidor
        // con un token que nunca va a servir. Un fallo transitorio (red o 5xx)
        // deja el refresh token en su lugar, así que ahí sí se sigue reintentando.
        if (!refrescado && this._tokenService.refreshToken === null) {
          const actual = this._sockets[id];
          if (!actual) return;
          actual.pendientes = [];
          this._resolverEspera(actual);
          this._cerradosPorSesion.add(id);
          this.cerrarSocket(id);
          return;
        }
      }

      this._conectar(id);
    }, espera);
  }

  /**
   * Sondea el servidor por HTTP y reporta la desconexión solo si responde: si el HTTP
   * también falla, el problema es la red del equipo y no el websocket, así que el
   * reporte queda pendiente para el siguiente ciclo de reintentos.
   * @param {string} id - El id del websocket.
   * @param {EntradaSocket} entrada - La entrada del websocket.
   * @param {number} codigo - El código de cierre de la última conexión.
   * @returns {Promise<void>} - Una promesa que se resuelve al terminar la comprobación (promise).
   */
  private async _confirmarYReportar$(
    id: string,
    entrada: EntradaSocket,
    codigo: number,
  ): Promise<void> {
    entrada.sondeando = true;
    const responde = await this._servidorResponde$().catch(() => false);
    entrada.sondeando = false;

    // Si mientras tanto el socket se cerró o reconectó (eso reinicia `reportado`), ya no
    // hay nada que reportar de esta caída
    if (this._sockets[id] !== entrada || !entrada.reportado) return;

    if (responde) this._reportarDesconexion(id, codigo);
    else entrada.reportado = false;
  }

  /**
   * Comprueba si el servidor responde por HTTP con una petición liviana a un endpoint
   * público. Cualquier respuesta cuenta, incluso un error HTTP (`no-cors` la deja
   * opaca): solo un fallo de red o el timeout indican que el servidor no es alcanzable.
   * @returns {Promise<boolean>} - Si el servidor respondió (promise).
   */
  private async _servidorResponde$(): Promise<boolean> {
    const controlador = new AbortController();
    const timer = setTimeout(() => controlador.abort(), TIMEOUT_SONDEO);
    try {
      await fetch(URL_SONDEO, {
        cache: 'no-store',
        mode: 'no-cors',
        signal: controlador.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Reporta la desconexión prolongada del websocket (para capturarla en Sentry).
   * @param {string} id - El id del websocket.
   * @param {number} codigo - El código de cierre de la última conexión.
   */
  private _reportarDesconexion(id: string, codigo: number): void {
    const minutos = VENTANA_RECONEXION / 60000;
    setTimeout(() => {
      throw new ErrorFrontend(
        `El socket con id ${id} no logró reconectarse tras ${minutos} minutos de reintentos (código de cierre ${codigo})`,
      );
    });
  }

  /**
   * Reintenta de inmediato la conexión de los websockets caídos, sin esperar la espera
   * acumulada del backoff.
   */
  private _reintentarAhora(): void {
    for (const id of Object.keys(this._sockets)) {
      const entrada = this._sockets[id];
      if (entrada.listo || entrada.socket !== undefined) continue;
      entrada.intentos = 0;
      this._limpiarTimers(entrada);
      this._conectar(id);
    }
  }

  /**
   * Resuelve la promesa de la primera conexión, si sigue pendiente.
   * @param {EntradaSocket} entrada - La entrada del websocket.
   */
  private _resolverEspera(entrada: EntradaSocket): void {
    entrada.resolver?.();
    entrada.resolver = undefined;
  }

  /**
   * Envía un mensaje por el websocket sin encolarlo.
   * @param {EntradaSocket} entrada - La entrada del websocket.
   * @param {DatosWebsocket} datos - El mensaje a enviar.
   * @returns {boolean} - Si el mensaje se pudo enviar.
   */
  private _enviarDirecto(entrada: EntradaSocket, datos: DatosWebsocket): boolean {
    if (entrada.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      entrada.socket.send(JSON.stringify(datos));
      return true;
    } catch {
      // El socket se cerró durante el envío; el handler de cierre reconecta
      return false;
    }
  }

  /**
   * Verifica que el socket siga siendo el vigente para el id especificado.
   * @param {string} id - El id del websocket.
   * @param {WebSocket} socket - El socket que disparó el evento.
   * @returns {boolean} - Si el socket sigue vigente.
   */
  private _esVigente(id: string, socket: WebSocket): boolean {
    return this._sockets[id]?.socket === socket;
  }

  /**
   * Inicia el intervalo de ping para mantener la conexión viva.
   * @param {string} id - El id del websocket.
   */
  private _iniciarPing(id: string): void {
    const entrada = this._sockets[id];
    if (!entrada) return;
    this._limpiarPing(entrada);
    entrada.pingTimer = setInterval(() => {
      this._enviarDirecto(entrada, { tipo: 'ping' });
    }, INTERVALO_PING);
  }

  /**
   * Limpia el temporizador de ping del websocket.
   * @param {EntradaSocket} entrada - La entrada del websocket.
   */
  private _limpiarPing(entrada: EntradaSocket): void {
    if (entrada.pingTimer !== undefined) clearInterval(entrada.pingTimer);
    entrada.pingTimer = undefined;
  }

  /**
   * Limpia los temporizadores de ping y reconexión del websocket.
   * @param {EntradaSocket} entrada - La entrada del websocket.
   */
  private _limpiarTimers(entrada: EntradaSocket): void {
    this._limpiarPing(entrada);
    if (entrada.reconexionTimer !== undefined) clearTimeout(entrada.reconexionTimer);
    entrada.reconexionTimer = undefined;
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this._alRecuperarConexion);
    document.removeEventListener('visibilitychange', this._alCambiarVisibilidad);
    for (const id of Object.keys(this._sockets)) this.cerrarSocket(id);
  }
}
